import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useStore } from '../../store';
import { Account, EmailLang } from '../../types';

const COLORS: Account['color'][] = ['teal', 'blue', 'purple', 'coral', 'amber'];
const COLOR_HEX: Record<string, string> = { teal: '#1D9E75', blue: '#378ADD', purple: '#7F77DD', coral: '#D85A30', amber: '#BA7517' };

interface Props {
  onClose: () => void;
  editAccount?: Account & { password?: string };
}

export default function AddAccountModal({ onClose, editAccount }: Props) {
  const { t } = useTranslation();
  const { addAccount, updateAccount, showToast } = useStore();
  const isEdit = !!editAccount;
  const [loading, setLoading] = useState(false);
  const [form, setForm] = useState({
    name: editAccount?.name || '',
    email: editAccount?.email || '',
    password: '',
    imapHost: editAccount?.imapHost || '',
    imapPort: String(editAccount?.imapPort || 993),
    smtpHost: editAccount?.smtpHost || '',
    // 2525, not 587. Port 587 is not reachable from this network at all — it does not refuse, it
    // black-holes — so the old default paired with a connection test that had no timeout produced a
    // page that simply never came back (owner hit it 2026-09-20). Every working mailbox in the firm
    // is on 2525.
    smtpPort: String(editAccount?.smtpPort || 2525),
    myLang: (editAccount?.myLang || 'en') as EmailLang,
    color: (editAccount?.color || 'teal') as Account['color'],
  });
  const set = (k: string, v: string) => setForm(p => ({ ...p, [k]: v }));
  const langOptions = (t('langOptions', { returnObjects: true }) as any[]) || [];

  const inputStyle = { width: '100%', padding: '7px 10px', fontSize: 13, borderRadius: 'var(--radius-md)', border: '1px solid var(--border-strong)', background: 'var(--c-gray-50)', color: 'var(--text-primary)', outline: 'none' };

  const handleSubmit = async () => {
    if (loading) return;
    if (!form.name || !form.email || !form.imapHost || !form.smtpHost) return;
    if (!isEdit && !form.password) return;
    setLoading(true);
    try {
      if (isEdit) {
        await updateAccount(editAccount!.id, {
          name: form.name,
          email: form.email,
          ...(form.password ? { password: form.password } : {}),
          imapHost: form.imapHost,
          imapPort: Number(form.imapPort),
          smtpHost: form.smtpHost,
          smtpPort: Number(form.smtpPort),
          myLang: form.myLang,
          color: form.color,
        });
        showToast('Account updated');
      } else {
        await addAccount({ name: form.name, email: form.email, password: form.password, imapHost: form.imapHost, imapPort: Number(form.imapPort), smtpHost: form.smtpHost, smtpPort: Number(form.smtpPort), myLang: form.myLang, color: form.color });
        showToast(t('accountAdded'));
      }
      onClose();
    } catch (e: any) {
      showToast(e?.message || 'Error');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.45)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 }} onClick={onClose}>
      <div style={{ background: 'var(--bg-surface)', borderRadius: 'var(--radius-xl)', border: '1px solid var(--border)', width: 460, maxWidth: '95vw', boxShadow: 'var(--shadow-md)', overflow: 'hidden' }} onClick={e => e.stopPropagation()}>
        <div style={{ padding: '18px 20px 14px', borderBottom: '1px solid var(--border)', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <h3 style={{ fontSize: 15, fontWeight: 600, color: 'var(--text-primary)' }}>{isEdit ? 'Edit Account' : t('modalTitle')}</h3>
          <button onClick={onClose} style={{ width: 32, height: 32, borderRadius: 'var(--radius-md)', border: 'none', background: 'transparent', color: 'var(--text-secondary)', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center' }}><i className="ti ti-x" style={{ fontSize: 16 }} aria-hidden /></button>
        </div>
        <div style={{ padding: '16px 20px', display: 'flex', flexDirection: 'column', gap: 10 }}>

          {/* Gmail OAuth button — only show when adding new account */}
          {!isEdit && (
            <>
              <button
                onClick={() => {
                  const token = localStorage.getItem('mf_token');
                  const myLang = 'en';
                  const color = form.color || 'teal';
                  const apiBase = process.env.REACT_APP_API_URL || 'http://localhost:3001/api';
                  window.location.href = `${apiBase}/gmail/auth?token=${token}&myLang=${myLang}&color=${color}`;
                }}
                style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 10, width: '100%', padding: '10px', borderRadius: 'var(--radius-md)', border: '1px solid var(--border)', background: '#fff', color: '#444', fontSize: 13, fontWeight: 600, cursor: 'pointer', boxShadow: '0 1px 3px rgba(0,0,0,0.1)' }}
              >
                <svg width="18" height="18" viewBox="0 0 18 18"><path fill="#4285F4" d="M17.64 9.2c0-.637-.057-1.251-.164-1.84H9v3.481h4.844c-.209 1.125-.843 2.078-1.796 2.717v2.258h2.908c1.702-1.567 2.684-3.874 2.684-6.615z"/><path fill="#34A853" d="M9 18c2.43 0 4.467-.806 5.956-2.184l-2.908-2.258c-.806.54-1.837.86-3.048.86-2.344 0-4.328-1.584-5.036-3.711H.957v2.332C2.438 15.983 5.482 18 9 18z"/><path fill="#FBBC05" d="M3.964 10.707c-.18-.54-.282-1.117-.282-1.707s.102-1.167.282-1.707V4.961H.957C.347 6.175 0 7.55 0 9s.347 2.825.957 4.039l3.007-2.332z"/><path fill="#EA4335" d="M9 3.58c1.321 0 2.508.454 3.44 1.345l2.582-2.58C13.463.891 11.426 0 9 0 5.482 0 2.438 2.017.957 4.961L3.964 7.293C4.672 5.166 6.656 3.58 9 3.58z"/></svg>
                Sign in with Gmail
              </button>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                <div style={{ flex: 1, height: 1, background: 'var(--border)' }} />
                <span style={{ fontSize: 11, color: 'var(--text-tertiary)', fontWeight: 500 }}>OR add manually</span>
                <div style={{ flex: 1, height: 1, background: 'var(--border)' }} />
              </div>
            </>
          )}

          <div style={{ display: 'flex', gap: 10 }}>
            <div style={{ flex: 1 }}><label style={{ fontSize: 11, fontWeight: 600, color: 'var(--text-secondary)', display: 'block', marginBottom: 4 }}>{t('displayName')}</label><input style={inputStyle} value={form.name} onChange={e => set('name', e.target.value)} placeholder="Jane Smith" /></div>
            <div style={{ flex: 1 }}><label style={{ fontSize: 11, fontWeight: 600, color: 'var(--text-secondary)', display: 'block', marginBottom: 4 }}>{t('emailAddress')}</label><input style={inputStyle} type="email" value={form.email} onChange={e => set('email', e.target.value)} placeholder="jane@company.com" /></div>
          </div>
          <div><label style={{ fontSize: 11, fontWeight: 600, color: 'var(--text-secondary)', display: 'block', marginBottom: 4 }}>{t('password')}{isEdit && <span style={{ fontWeight: 400, marginLeft: 4 }}>(leave blank to keep current)</span>}</label><input style={inputStyle} type="password" value={form.password} onChange={e => set('password', e.target.value)} placeholder="••••••••" /></div>
          <div style={{ display: 'flex', gap: 10 }}>
            <div style={{ flex: 1 }}><label style={{ fontSize: 11, fontWeight: 600, color: 'var(--text-secondary)', display: 'block', marginBottom: 4 }}>{t('imapServer')}</label><input style={inputStyle} value={form.imapHost} onChange={e => set('imapHost', e.target.value)} placeholder="imap.company.com" /></div>
            <div style={{ maxWidth: 90 }}><label style={{ fontSize: 11, fontWeight: 600, color: 'var(--text-secondary)', display: 'block', marginBottom: 4 }}>{t('imapPort')}</label><input style={inputStyle} value={form.imapPort} onChange={e => set('imapPort', e.target.value)} placeholder="993" /></div>
          </div>
          <div style={{ display: 'flex', gap: 10 }}>
            <div style={{ flex: 1 }}><label style={{ fontSize: 11, fontWeight: 600, color: 'var(--text-secondary)', display: 'block', marginBottom: 4 }}>{t('smtpServer')}</label><input style={inputStyle} value={form.smtpHost} onChange={e => set('smtpHost', e.target.value)} placeholder="smtp.company.com" /></div>
            <div style={{ maxWidth: 90 }}><label style={{ fontSize: 11, fontWeight: 600, color: 'var(--text-secondary)', display: 'block', marginBottom: 4 }}>{t('smtpPort')}</label><input style={inputStyle} value={form.smtpPort} onChange={e => set('smtpPort', e.target.value)} placeholder="587" /></div>
          </div>
          <div><label style={{ fontSize: 11, fontWeight: 600, color: 'var(--text-secondary)', display: 'block', marginBottom: 4 }}>{t('workingLanguage')}</label>
            <select style={inputStyle} value={form.myLang} onChange={e => set('myLang', e.target.value)}>
              {langOptions.map((opt: any) => <option key={opt.value} value={opt.value}>{opt.label}</option>)}
            </select>
          </div>
          <div><label style={{ fontSize: 11, fontWeight: 600, color: 'var(--text-secondary)', display: 'block', marginBottom: 6 }}>Account color</label>
            <div style={{ display: 'flex', gap: 8 }}>
              {COLORS.map(c => <button key={c} onClick={() => set('color', c)} style={{ width: 28, height: 28, borderRadius: '50%', background: COLOR_HEX[c], border: form.color === c ? '3px solid var(--text-primary)' : '3px solid transparent', cursor: 'pointer' }} aria-label={c} />)}
            </div>
          </div>
        </div>
        <div style={{ padding: '12px 20px', borderTop: '1px solid var(--border)', display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
          <button onClick={onClose} disabled={loading} style={{ padding: '7px 14px', fontSize: 13, borderRadius: 'var(--radius-md)', border: '1px solid var(--border)', background: 'transparent', color: 'var(--text-secondary)', cursor: 'pointer' }}>{t('cancel')}</button>
          <button onClick={handleSubmit} disabled={loading} style={{ padding: '7px 18px', fontSize: 13, borderRadius: 'var(--radius-md)', border: 'none', background: loading ? 'var(--text-tertiary)' : 'var(--accent)', color: '#fff', cursor: loading ? 'not-allowed' : 'pointer', fontWeight: 600 }}>
            {loading ? 'Connecting…' : (isEdit ? 'Save changes' : t('connectVerify'))}
          </button>
        </div>
      </div>
    </div>
  );
}
